import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Percent, AlertTriangle } from "lucide-react";

// Companion to BetaOverridesNeedingReview: the LISTING-level fee overrides. Lists every listing
// carrying an active admin fee override (0% promo or a custom rate) so admins have ONE place to see
// all active overrides — account-level (above) and listing-level (here) — together. Review-flagged
// rows (indefinite, or past a scheduled review date) are surfaced in volt; the override keeps
// applying at its current rate and is NEVER silently reverted. Each chip deep-links to Admin ›
// Listings filtered to that listing, where the Percent drawer adjusts/clears it.
// Backed by GET /api/admin/platform/listings/fee-overrides.
export function ListingFeeOverridesReview() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/admin/platform/listings/fee-overrides")
      .then(({ data }) => setRows(data?.listings || []))
      .catch(() => setRows([]));
  }, []);

  if (!rows || rows.length === 0) return null;

  const reviewCount = rows.filter((r) => r.review_flag).length;

  return (
    <div className="pt-1" data-testid="listing-fee-overrides-review">
      <div className="inline-flex flex-col gap-1 border border-olive-bright/30 bg-olive/5 px-3 py-2 max-w-xl">
        <div className="flex items-center gap-1.5 font-mono uppercase text-[10px] tracking-wide text-olive-bright">
          <Percent className="w-3.5 h-3.5" /> Listing fee overrides ({rows.length})
          {reviewCount > 0 && (
            <span className="inline-flex items-center gap-1 text-volt normal-case" data-testid="listing-fee-overrides-review-count">
              <AlertTriangle className="w-3 h-3" /> {reviewCount} need review
            </span>
          )}
        </div>
        <span className="font-mono text-[10px] text-muted-foreground normal-case">
          Active promotional overrides on individual listings. Click to adjust / clear in Listings.
        </span>
        <div className="flex flex-wrap gap-1" data-testid="listing-fee-overrides-review-list">
          {rows.map((l) => {
            const fo = l.fee_override || {};
            const flag = !!l.review_flag;
            const cls = flag ? "border-volt/40 text-volt/90 hover:bg-volt/10" : "border-olive-bright/30 text-olive-bright/90 hover:bg-olive/10";
            return (
              <Link key={l.listing_id} data-testid={`listing-fee-override-review-${l.listing_id}`}
                to={`/admin/listings?status=all&search=${encodeURIComponent(l.listing_id)}`}
                title={`${l.title || l.listing_id} · @${l.seller_username || "?"} · ${Number(fo.rate ?? 0)}%${fo.expires_at ? "" : " · indefinite"}`}
                className={`font-mono text-[10px] px-1.5 py-0.5 border normal-case transition-colors ${cls}`}>
                {flag && <AlertTriangle className="inline w-2.5 h-2.5 mr-0.5 -mt-0.5" />}
                @{l.seller_username || "?"} · {Number(fo.rate ?? 0)}%
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
